import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { TextAttributeCfgUIElement } from '@/elements/attributes/TextAttribute/TextAttribute';
import { BooleanAttributeCfgUIElement } from '@/elements/attributes/BooleanAttribute/BooleanAttribute';
import { IntegerAttributeCfgUIElement } from '@/elements/attributes/IntegerAttribute/IntegerAttribute';
import ComponentSwitchCfgUIElement from '@/elements/ComponentElement/ComponentSwitch';
import {
  MockTextAttributeState,
  MockBooleanAttributeState,
  MockIntegerAttributeState,
} from '../mocks/MockAttributeStates';
import { MockComponentLookup } from '../mocks/MockComponentStates';
import {
  // Existing configs
  mockToggleAttributeConfig,
  mockQuantityAttributeConfig,
  mockCoffeeMachineComponent,
  mockAccessoryComponent,
  // Grid layout variations
  mockCardGridFull,
  mockCardGridImageTitle,
  mockCardGridTitleOnly,
  mockCardGridImageOnly,
  // List layout variations
  mockCardListFull,
  mockCardListTitleDesc,
  mockCardListTitleOnly,
  mockCardListImageTitle,
  // Attribute details variations
  mockAttributeDetailsFull,
  mockAttributeDetailsTitleDesc,
  mockAttributeDetailsTitleOnly,
} from '../mockData';
import styles from './StyleGuideOptions.module.css';

export const StyleGuideOptions: React.FC = observer(function StyleGuideOptions() {
  // ============================================================================
  // GRID LAYOUT VARIATIONS
  // ============================================================================

  // Grid: Full (Image + Title + Description)
  const [gridFullState] = useState(() => new MockTextAttributeState(mockCardGridFull));

  // Grid: Image + Title (no description)
  const [gridImageTitleState] = useState(() => new MockTextAttributeState(mockCardGridImageTitle));

  // Grid: Title only (no image)
  const [gridTitleOnlyState] = useState(() => new MockTextAttributeState(mockCardGridTitleOnly));

  // Grid: Image only (no title)
  const [gridImageOnlyState] = useState(() => new MockTextAttributeState(mockCardGridImageOnly));

  // ============================================================================
  // LIST LAYOUT VARIATIONS
  // ============================================================================

  // List: Full (Image + Title + Description)
  const [listFullState] = useState(() => new MockTextAttributeState(mockCardListFull));

  // List: Title + Description (no image)
  const [listTitleDescState] = useState(() => new MockTextAttributeState(mockCardListTitleDesc));

  // List: Title only
  const [listTitleOnlyState] = useState(() => new MockTextAttributeState(mockCardListTitleOnly));

  // List: Image + Title (no description)
  const [listImageTitleState] = useState(() => new MockTextAttributeState(mockCardListImageTitle));

  // ============================================================================
  // ATTRIBUTE DETAILS VARIATIONS
  // ============================================================================

  // Details: Full (Image + Title + Description)
  const [detailsFullState] = useState(() => new MockTextAttributeState(mockAttributeDetailsFull));

  // Details: Title + Description
  const [detailsTitleDescState] = useState(() => new MockTextAttributeState(mockAttributeDetailsTitleDesc));

  // Details: Title only
  const [detailsTitleOnlyState] = useState(() => new MockTextAttributeState(mockAttributeDetailsTitleOnly));

  // ============================================================================
  // BOOLEAN & INTEGER ATTRIBUTES
  // ============================================================================

  // Boolean Attribute - Toggle
  const [toggleState] = useState(() => new MockBooleanAttributeState(mockToggleAttributeConfig));

  // Integer Attribute - Quantity
  const [integerState] = useState(() => new MockIntegerAttributeState(mockQuantityAttributeConfig));

  // ============================================================================
  // COMPONENT SWITCHES
  // ============================================================================

  // Component: Enabled
  const [componentEnabledLookup] = useState(() => new MockComponentLookup(mockCoffeeMachineComponent.lookupConfig));

  // Component: Disabled
  const [componentDisabledLookup] = useState(() => new MockComponentLookup(mockAccessoryComponent.lookupConfig));

  return (
    <div className={styles.section}>
      {/* ================================================================== */}
      {/* SECTION 1: ATTRIBUTE CARDS - GRID LAYOUT */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>1. Attribute Cards - Grid Layout</h2>
        <p className={styles.sectionDescription}>
          Grid layout displays options as vertical cards in responsive columns (2-4 based on width).
        </p>
      </div>

      {/* Grid: Full (Image + Title + Description) */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Grid: Image + Title + Description</h3>
        <p className={styles.optionDescription}>
          All card elements visible. Best for rich content with images and detailed descriptions.
        </p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={gridFullState as any} />
        </div>
      </div>

      {/* Grid: Image + Title */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Grid: Image + Title</h3>
        <p className={styles.optionDescription}>
          No description shown. Good for visual options where names are self-explanatory.
        </p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={gridImageTitleState as any} />
        </div>
      </div>

      {/* Grid: Title only */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Grid: Title Only</h3>
        <p className={styles.optionDescription}>No image or description. Minimal appearance for simple text options.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={gridTitleOnlyState as any} />
        </div>
      </div>

      {/* Grid: Image only */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Grid: Image Only</h3>
        <p className={styles.optionDescription}>
          Only images visible. Perfect for color swatches or icon-based selections.
        </p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={gridImageOnlyState as any} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION 2: ATTRIBUTE CARDS - LIST LAYOUT */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>2. Attribute Cards - List Layout</h2>
        <p className={styles.sectionDescription}>
          List layout displays options as horizontal cards in a single column.
        </p>
      </div>

      {/* List: Full */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>List: Image + Title + Description</h3>
        <p className={styles.optionDescription}>
          All elements in horizontal layout. Good for detailed option information.
        </p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={listFullState as any} />
        </div>
      </div>

      {/* List: Title + Description */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>List: Title + Description</h3>
        <p className={styles.optionDescription}>No image. Text-focused layout for descriptive options.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={listTitleDescState as any} />
        </div>
      </div>

      {/* List: Title only */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>List: Title Only</h3>
        <p className={styles.optionDescription}>Minimal list layout with just labels.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={listTitleOnlyState as any} />
        </div>
      </div>

      {/* List: Image + Title */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>List: Image + Title</h3>
        <p className={styles.optionDescription}>No description. Clean visual list with thumbnails.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={listImageTitleState as any} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION 3: ATTRIBUTE WITH DETAILS PANEL */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>3. Attribute with Details Panel</h2>
        <p className={styles.sectionDescription}>
          Shows additional details below the selection when an option is selected.
        </p>
      </div>

      {/* Details: Full */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Details: Image + Title + Description</h3>
        <p className={styles.optionDescription}>Full details panel appears below when an option is selected.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={detailsFullState as any} />
        </div>
      </div>

      {/* Details: Title + Description */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Details: Title + Description</h3>
        <p className={styles.optionDescription}>Details panel without image. Good for text-heavy explanations.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={detailsTitleDescState as any} />
        </div>
      </div>

      {/* Details: Title only */}
      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Details: Title Only</h3>
        <p className={styles.optionDescription}>Minimal details panel showing just the selected option title.</p>
        <div className={styles.demo}>
          <TextAttributeCfgUIElement state={detailsTitleOnlyState as any} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION 4: BOOLEAN ATTRIBUTES */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>4. Boolean Attributes</h2>
        <p className={styles.sectionDescription}>Toggle switches for yes/no options.</p>
      </div>

      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Boolean Toggle</h3>
        <p className={styles.optionDescription}>Simple on/off toggle with label.</p>
        <div className={styles.demo}>
          <BooleanAttributeCfgUIElement state={toggleState as any} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION 5: INTEGER ATTRIBUTES */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>5. Integer Attributes</h2>
        <p className={styles.sectionDescription}>Numeric selection for quantities or counts.</p>
      </div>

      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Integer Quantity</h3>
        <p className={styles.optionDescription}>Numeric value selection.</p>
        <div className={styles.demo}>
          <IntegerAttributeCfgUIElement state={integerState as any} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION 6: COMPONENT SWITCHES */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>6. Component Switches</h2>
        <p className={styles.sectionDescription}>
          Toggle for optional components. Shows component name and description.
        </p>
      </div>

      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Component: Enabled</h3>
        <p className={styles.optionDescription}>Optional component that is enabled by default.</p>
        <div className={styles.demo}>
          <ComponentSwitchCfgUIElement
            lookup={componentEnabledLookup as any}
            viewElement={mockCoffeeMachineComponent.viewElement}
          />
        </div>
      </div>

      <div className={styles.optionDemo}>
        <h3 className={styles.optionTitle}>Component: Disabled</h3>
        <p className={styles.optionDescription}>Optional component that is disabled by default.</p>
        <div className={styles.demo}>
          <ComponentSwitchCfgUIElement
            lookup={componentDisabledLookup as any}
            viewElement={mockAccessoryComponent.viewElement}
          />
        </div>
      </div>
    </div>
  );
});
